/**
 * useChoiceSystem - 選擇系統 React Hook
 * 連接 gameStore 的選擇系統到 React UI
 */

import { useCallback, useMemo } from 'react';
import { useGameStore } from '../../store/gameStore';
import type { ChoiceResult, PendingChoice } from '../../core/effects';
import type { Card } from '../../types';

export interface UseChoiceSystemReturn {
  // 狀態
  pendingChoice: PendingChoice | null;
  hasPendingChoice: boolean;
  validTargets: Card[];
  
  // 選項選擇
  selectOption: (optionId: string) => { success: boolean; error?: string };
  
  // 目標/卡牌選擇
  selectCards: (cardIds: string[]) => { success: boolean; error?: string };
  selectCard: (cardId: string) => { success: boolean; error?: string };
  
  // 位置選擇
  selectPosition: (position: { side: 'player1' | 'player2'; row: 'front' | 'back'; column: 1 | 2 | 3 | 4 | 5 }) => { success: boolean; error?: string };
  
  // 數字選擇
  selectNumber: (num: number) => { success: boolean; error?: string };
  
  // 取消
  cancel: () => void;
  
  // 輔助
  isTargetValid: (cardId: string) => boolean;
  getChoiceTypeLabel: () => string;
}

/**
 * 選擇系統 Hook
 */
export function useChoiceSystem(): UseChoiceSystemReturn {
  const pendingChoice = useGameStore(state => state.pendingChoice);
  const submitChoice = useGameStore(state => state.submitChoice);
  const cancelChoice = useGameStore(state => state.cancelChoice);
  const getValidTargets = useGameStore(state => state.getValidTargets);
  
  const hasPendingChoice = pendingChoice !== null;
  
  // 取得有效目標
  const validTargets = useMemo(() => {
    if (!pendingChoice || !pendingChoice.filter) return [];
    return getValidTargets();
  }, [pendingChoice, getValidTargets]);
  
  // 有效目標 ID Set (用於快速查詢)
  const validTargetIds = useMemo(() => {
    return new Set(validTargets.map(c => c.id));
  }, [validTargets]);
  
  // 選擇選項
  const selectOption = useCallback((optionId: string) => {
    if (!pendingChoice) {
      return { success: false, error: '沒有待決選擇' };
    }
    
    const result: ChoiceResult = {
      choiceId: pendingChoice.id,
      type: 'option',
      selectedOptionId: optionId,
    };
    
    return submitChoice(result);
  }, [pendingChoice, submitChoice]);
  
  // 選擇卡牌（複數）
  const selectCards = useCallback((cardIds: string[]) => {
    if (!pendingChoice) {
      return { success: false, error: '沒有待決選擇' };
    }
    
    const result: ChoiceResult = {
      choiceId: pendingChoice.id,
      type: pendingChoice.type,
      selectedCardIds: cardIds,
    };
    
    return submitChoice(result);
  }, [pendingChoice, submitChoice]);
  
  // 選擇單張卡牌
  const selectCard = useCallback((cardId: string) => {
    return selectCards([cardId]);
  }, [selectCards]);
  
  // 選擇位置
  const selectPosition = useCallback((position: { side: 'player1' | 'player2'; row: 'front' | 'back'; column: 1 | 2 | 3 | 4 | 5 }) => {
    if (!pendingChoice) {
      return { success: false, error: '沒有待決選擇' };
    }
    
    const result: ChoiceResult = {
      choiceId: pendingChoice.id,
      type: 'position',
      selectedPosition: position,
    };
    
    return submitChoice(result);
  }, [pendingChoice, submitChoice]);
  
  // 選擇數字
  const selectNumber = useCallback((num: number) => {
    if (!pendingChoice) {
      return { success: false, error: '沒有待決選擇' };
    }
    
    const result: ChoiceResult = {
      choiceId: pendingChoice.id,
      type: 'number',
      selectedNumber: num,
    };
    
    return submitChoice(result);
  }, [pendingChoice, submitChoice]);
  
  // 取消選擇
  const cancel = useCallback(() => {
    cancelChoice();
  }, [cancelChoice]);
  
  // 檢查目標是否有效
  const isTargetValid = useCallback((cardId: string) => {
    return validTargetIds.has(cardId);
  }, [validTargetIds]);
  
  // 取得選擇類型標籤
  const getChoiceTypeLabel = useCallback(() => {
    if (!pendingChoice) return '';
    
    switch (pendingChoice.type) {
      case 'option':
        return '選擇選項';
      case 'target':
        return '選擇目標';
      case 'card':
        return '選擇卡牌';
      case 'position':
        return '選擇位置';
      case 'number':
        return '選擇數量';
      default:
        return '選擇';
    }
  }, [pendingChoice]);
  
  return {
    pendingChoice,
    hasPendingChoice,
    validTargets,
    selectOption,
    selectCards,
    selectCard,
    selectPosition,
    selectNumber,
    cancel,
    isTargetValid,
    getChoiceTypeLabel,
  };
}

export default useChoiceSystem;
